import type { ReactNode } from 'react'
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { useTrackerAnalytics } from '@/api/hooks/useTracker'
import type {
  AnalyticsData, AnalyticsTimelinePoint, AnalyticsCountry,
} from '@/api/hooks/useTracker'

// Country centroid lookup table (lat/lon for CSS positioning on world map)
const CENTROIDS: Record<string, [number, number]> = {
  br: [-14.2, -51.9], us: [37.1, -95.7], pt: [39.4, -8.2],
  ar: [-38.4, -63.6], ao: [-11.2, 17.9], mx: [23.6, -102.5],
  co: [4.6, -74.1], gb: [55.4, -3.4], ca: [56.1, -106.3],
  de: [51.2, 10.5], fr: [46.2, 2.2], es: [40.5, -3.7],
  it: [41.9, 12.6], cl: [-35.7, -71.5], pe: [-9.2, -75.0],
  uy: [-32.5, -55.8], py: [-23.4, -58.4], bo: [-16.3, -63.6],
  cn: [35.9, 104.2], jp: [36.2, 138.3], au: [-25.3, 133.8],
  in: [20.6, 78.9], ru: [61.5, 105.3], za: [-30.6, 22.9],
}

function latLonToCSS(lat: number, lon: number) {
  return {
    top: `${((90 - lat) / 180) * 100}%`,
    left: `${((lon + 180) / 360) * 100}%`,
  }
}

const COUNTRY_FLAG_BASE = '/whos/flags/'
const DEVICE_ICONS: Record<string, string> = {
  android: '/whos/android.png',
  apple: '/whos/apple.png',
  windows: '/whos/windows.png',
  other: '/whos/windows.png',
}

const CC_EMOJI: Record<string, string> = {
  br: '🇧🇷', us: '🇺🇸', pt: '🇵🇹', ar: '🇦🇷', ao: '🇦🇴',
  mx: '🇲🇽', co: '🇨🇴', gb: '🇬🇧', ca: '🇨🇦', de: '🇩🇪',
  fr: '🇫🇷', es: '🇪🇸', it: '🇮🇹', cl: '🇨🇱', pe: '🇵🇪',
}
const hasSvg = ['br', 'us', 'pt', 'ar', 'ao', 'mx', 'co', 'gb', 'ca', 'de', 'fr', 'es']

const WHOSPULSE_KEYFRAMES = `@keyframes whospulse{0%,100%{opacity:1}50%{opacity:.5}}`

// ─── Header ─────────────────────────────────────────────────────────────────
function AnalyticsHeader({ online }: { online: number }) {
  return (
    <div style={{
      background: '#1a1a1a',
      borderBottom: '1px solid #333',
      padding: '6px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <span style={{ fontSize: 18 }}>🤖</span>
      <span style={{ fontWeight: 700, color: '#ddd', fontSize: 14 }}>
        Dashboard{' '}
        <span style={{
          color: '#e94560',
          animation: online > 0 ? 'whospulse 2s ease-in-out infinite' : undefined,
        }}>
          ({online})
        </span>
      </span>
    </div>
  )
}

// ─── Panel wrapper ───────────────────────────────────────────────────────────
function Panel({ title, icon, children }: { title: string; icon: string; children: ReactNode }) {
  return (
    <div style={{
      background: '#1a1a1a',
      border: '1px solid #2a2a2a',
      borderRadius: 4,
      overflow: 'hidden',
    }}>
      <div style={{
        background: '#222',
        borderBottom: '1px solid #2a2a2a',
        padding: '5px 10px',
        fontSize: 11,
        fontWeight: 700,
        color: '#888',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <span>{icon}</span> {title}
      </div>
      <div style={{ padding: 10 }}>{children}</div>
    </div>
  )
}

// ─── History Panel ───────────────────────────────────────────────────────────
function HistoryPanel({ timeline }: { timeline: AnalyticsTimelinePoint[] }) {
  const hasMinMax = timeline.some(t => t.online_min !== null)

  return (
    <Panel title="History" icon="📊">
      <div style={{ height: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={timeline} margin={{ top: 4, right: 4, bottom: 0, left: 24 }}>
            <defs>
              <linearGradient id="gMax" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6600" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#ff6600" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="gAvg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#44bb44" stopOpacity={0.7} />
                <stop offset="95%" stopColor="#44bb44" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="gMin" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2244ff" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#2244ff" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" vertical={false} />
            <XAxis dataKey="hour" tick={{ fill: '#555', fontSize: 9 }} tickLine={false} axisLine={false} interval={3} />
            <YAxis tick={{ fill: '#555', fontSize: 9 }} tickLine={false} axisLine={false} width={22} />
            <Tooltip
              contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 4, fontSize: 11 }}
              labelStyle={{ color: '#aaa' }}
              itemStyle={{ color: '#ddd' }}
            />
            {hasMinMax && (
              <Area type="monotone" dataKey="online_max" stroke="#ff6600" strokeWidth={1}
                fill="url(#gMax)" connectNulls={false} dot={false} name="max" />
            )}
            <Area type="monotone" dataKey="online_avg" stroke="#44bb44" strokeWidth={1.5}
              fill="url(#gAvg)" connectNulls={false} dot={false} name="avg" />
            {hasMinMax && (
              <Area type="monotone" dataKey="online_min" stroke="#2244ff" strokeWidth={1}
                fill="url(#gMin)" connectNulls={false} dot={false} name="min" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: '#555' }}>
        {hasMinMax && <span style={{ color: '#ff6600' }}>■ max</span>}
        <span style={{ color: '#44bb44' }}>■ avg</span>
        {hasMinMax && <span style={{ color: '#2244ff' }}>■ min</span>}
      </div>
    </Panel>
  )
}

// ─── Readers Panel ───────────────────────────────────────────────────────────
function ReadersPanel({ data }: { data: AnalyticsData['readers'] }) {
  return (
    <Panel title="Readers" icon="📖">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.map((r, i) => (
          <div key={r.path} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '3px 0', borderBottom: '1px solid #1e1e1e' }}>
            <span style={{ color: '#e94560', fontWeight: 900, fontSize: i === 0 ? 20 : 14, minWidth: 28, lineHeight: '1.2' }}>
              {r.count}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {i === 0 && <div style={{ color: '#ff6600', fontSize: 9, marginBottom: 1 }}>★★★</div>}
              <div style={{ fontSize: 11, color: '#bbb', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.title || r.path}
              </div>
              <div style={{ fontSize: 9, color: '#555' }}>{r.pct}%</div>
            </div>
          </div>
        ))}
        {data.length === 0 && <p style={{ fontSize: 11, color: '#555', textAlign: 'center' as const, padding: '12px 0' }}>Sem dados</p>}
      </div>
    </Panel>
  )
}

// ─── Maps Panel ──────────────────────────────────────────────────────────────
function MapsPanel({ countries }: { countries: AnalyticsCountry[] }) {
  const maxCount = countries[0]?.count || 1
  return (
    <Panel title="Maps" icon="🗺">
      <div style={{ position: 'relative' as const, width: '100%', paddingBottom: '50%' }}>
        <img
          src="/whos/dashmap.png"
          alt="world map"
          style={{ position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'fill' as const, opacity: 0.85 }}
        />
        {countries.map((c) => {
          const centroid = CENTROIDS[c.cc]
          if (!centroid) return null
          const pos = latLonToCSS(centroid[0], centroid[1])
          const intensity = c.count / maxCount
          const size = Math.max(6, Math.round(intensity * 18))
          const opacity = 0.4 + intensity * 0.6
          return (
            <div
              key={c.cc}
              title={`${c.name}: ${c.count}`}
              style={{
                position: 'absolute' as const,
                transform: 'translate(-50%, -50%)',
                width: size,
                height: size,
                borderRadius: '50%',
                background: intensity > 0.5 ? '#e94560' : '#ff9900',
                opacity,
                boxShadow: `0 0 ${size}px ${intensity > 0.5 ? '#e94560' : '#ff9900'}`,
                cursor: 'default' as const,
                ...pos,
              }}
            />
          )
        })}
      </div>
    </Panel>
  )
}

// ─── Devices Panel ───────────────────────────────────────────────────────────
function DevicesPanel({ devices, deviceHistory }: {
  devices: AnalyticsData['devices']
  deviceHistory: AnalyticsData['device_history']
}) {
  const total = devices.reduce((s, d) => s + d.count, 0) || 1
  const icons: string[] = []
  devices.forEach(d => {
    const n = Math.min(Math.round((d.count / total) * 60), 60 - icons.length)
    for (let i = 0; i < n; i++) icons.push(d.type)
  })

  return (
    <Panel title="Devices" icon="💻">
      {deviceHistory.length > 0 && (
        <div style={{ height: 60, marginBottom: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={deviceHistory} margin={{ top: 2, right: 2, bottom: 0, left: 0 }}>
              <Line type="monotone" dataKey="android" stroke="#a4c639" strokeWidth={1.5} dot={false} name="Android" />
              <Line type="monotone" dataKey="apple" stroke="#aaaaaa" strokeWidth={1.5} dot={false} name="iOS" />
              <Line type="monotone" dataKey="windows" stroke="#00a4ef" strokeWidth={1.5} dot={false} name="Windows" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 3 }}>
        {icons.slice(0, 60).map((type, i) => (
          <div key={i} style={{
            width: 26, height: 26,
            borderRadius: '50%',
            border: `2px solid ${type === 'android' ? '#a4c639' : type === 'apple' ? '#aaa' : '#00a4ef'}`,
            background: '#222',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <img src={DEVICE_ICONS[type]} alt={type} style={{ width: 18, height: 18, objectFit: 'contain' as const }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10 }}>
        <span style={{ color: '#a4c639' }}>■ Android</span>
        <span style={{ color: '#aaaaaa' }}>■ iOS</span>
        <span style={{ color: '#00a4ef' }}>■ Windows</span>
      </div>
    </Panel>
  )
}

// ─── Views Panel ─────────────────────────────────────────────────────────────
function ViewsPanel({ data }: { data: AnalyticsData['hourly_views'] }) {
  return (
    <Panel title="Views" icon="👁">
      <div style={{ height: 100 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 2, right: 2, bottom: 0, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" vertical={false} />
            <XAxis dataKey="hour" tick={{ fill: '#555', fontSize: 8 }} tickLine={false} axisLine={false} interval={3} />
            <YAxis tick={{ fill: '#555', fontSize: 8 }} tickLine={false} axisLine={false} width={20} />
            <Tooltip
              contentStyle={{ background: '#111', border: '1px solid #333', fontSize: 11 }}
              labelStyle={{ color: '#aaa' }}
            />
            <Bar dataKey="views" fill="#aa4400" radius={[1, 1, 0, 0]} name="Views" />
            <Bar dataKey="sessions" fill="#3b82f6" radius={[1, 1, 0, 0]} name="Sessions" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: '#555' }}>
        <span style={{ color: '#aa4400' }}>■ Views</span>
        <span style={{ color: '#3b82f6' }}>■ Sessions</span>
      </div>
    </Panel>
  )
}

// ─── Countries Panel ─────────────────────────────────────────────────────────
function CountriesPanel({ countries }: { countries: AnalyticsCountry[] }) {
  return (
    <Panel title="Countries" icon="🌍">
      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
        {countries.slice(0, 12).map((c) => (
          <div key={c.cc} style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 2, width: 46 }}>
            {hasSvg.includes(c.cc) ? (
              <img
                src={`${COUNTRY_FLAG_BASE}${c.cc}.svg`}
                alt={c.cc}
                style={{ width: 40, height: 26, objectFit: 'cover' as const, borderRadius: 2, border: '1px solid #333' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            ) : (
              <span style={{ fontSize: 26, lineHeight: '26px' }}>{CC_EMOJI[c.cc] ?? '🌐'}</span>
            )}
            <span style={{ fontSize: 8, color: '#555', textAlign: 'center' as const, maxWidth: 46, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
              {c.name}
            </span>
            <span style={{ fontSize: 9, color: '#888', fontFamily: 'monospace' }}>{c.count}</span>
          </div>
        ))}
      </div>
    </Panel>
  )
}

// ─── Main AnalyticsTab ────────────────────────────────────────────────────────
export default function AnalyticsTab({ siteId }: { siteId: string }) {
  const { data, isLoading } = useTrackerAnalytics(siteId)

  if (isLoading || !data) {
    return (
      <div style={{ background: '#111', padding: 20, textAlign: 'center' as const, color: '#555', fontSize: 12 }}>
        Carregando analytics...
      </div>
    )
  }

  return (
    <div style={{ background: '#111', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 12 }}>
      <style>{WHOSPULSE_KEYFRAMES}</style>
      <AnalyticsHeader online={data.online} />

      <div style={{ padding: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {/* History — full width */}
        <div style={{ gridColumn: '1 / -1' }}>
          <HistoryPanel timeline={data.timeline} />
        </div>

        {/* Col esquerda: Readers, Views, Countries */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
          <ReadersPanel data={data.readers} />
          <ViewsPanel data={data.hourly_views} />
          <CountriesPanel countries={data.countries} />
        </div>

        {/* Col direita: Maps + Devices */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
          <MapsPanel countries={data.countries} />
          <DevicesPanel devices={data.devices} deviceHistory={data.device_history} />
        </div>
      </div>
    </div>
  )
}
